import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useOfferCheckContext} from 'components/Accommodation/Rooms/OfferCheckContext'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {useIsOptionalSignInEligible} from 'hooks/useIsOptionalSignInEligible'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {getRoomOfferAnalyticsContexts} from 'middleware/analytics/selectors'
import {RoomOfferUrlError} from 'modules/accommodation/errors'
import {getRoomsActiveFilters} from 'modules/accommodation/selectors'
import {getCheckoutLink, getOfferCheckParams} from 'modules/accommodation/utils'
import {
  getCurrencyCode,
  getLocaleCode,
  getLocalTaxesIncluded,
  getTaxesIncluded,
  getUserCountryCode
} from 'modules/meta/selectors'
import {
  getDealDetailsOverlayVisibility,
  getRoomDetailOverlayVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {
  getAnchorPriceTotal,
  getNumberOfGuests,
  getNumberOfRooms,
  getShouldConfirmBookOnApp
} from 'modules/sapiSearch/selectors'
import {getIsUserSearch} from 'modules/search/selectors'
import {
  getCommonPaymentPageLandingParams,
  getHotelResult,
  getRoomOfferAccessConfig,
  getRoomOfferAppLockedConfig
} from 'modules/searchApi/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {ProviderRedirectQueryParamsType} from 'types/ProviderRedirect'
import {getTimezone} from 'utils'
import {imageProvider} from 'utils/imageProvider'
import {generateRedirectLink} from 'utils/offers'

import {SIZES} from '@vio/atlas/Image'
import {getAnonymousId} from '@vio/core/src/_web/anonymousId'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {getSubBrandKeyFromUrl} from '@vio/core/src/_web/brand/utils'
import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {getQueryStringObject} from '@vio/core/src/_web/utils/url'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {
  getIsPrivateDeal,
  getWhiteLabelUserId,
  selectUserId
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook -- Using getBrandByCode directly for performance - we need brand object to get subBrand
import {getBrandByCode} from '@vio/core/src/brand/config'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {getLinkByType} from '@vio/core/src/offer/business/getLinkByType'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {ActionLinkTypes} from '@vio/core/src/offer/types/actionLink'
import {Offer} from '@vio/core/src/offer/types/offer'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {OfferCheckAPIResponse} from '@vio/core/src/offer/types/OfferCheck'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {roomsFilterListToMap} from '@vio/core/src/room/business/utils/filters'
import {getRoomlessVersion} from '@vio/core/src/room/business/utils/roomless'
import {Room, SplitBooking} from '@vio/core/src/room/types/room'
import {getEpochSeconds} from '@vio/core/src/utils/date'
import {BaseUrlCheckoutParams} from '@vio/core/src/utils/url/types/UrlParams'
import {useRoomDisplayName} from '@vio/shared/src/accommodation/RoomDisplayName'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

import {useGetComposedOfferCheckAPIParamsBuilder} from './useGetComposedOfferCheckAPIParams'

const NAVIGATION_DELAY = 50

const captureSentryError = (offer?: Offer, splitBooking?: SplitBooking) => {
  if (typeof Sentry !== 'undefined')
    Sentry.captureException(
      new RoomOfferUrlError(
        'Checkout link was not found in the SAPI offer',
        {}
      ),
      {extra: {offer, splitBooking}}
    )
}

export const useRoomOfferInteraction = (props: {
  hotelId: string
  roomId: string
  /** Room holding the offer, read for the roomless version */
  room?: Pick<Room, 'name'>
  offer: {offer?: Offer; splitBooking?: SplitBooking}
  component: string
  hasBookingHint?: boolean
}) => {
  const dispatch = useDispatch()
  const {
    hotelId,
    roomId,
    room,
    offer: {offer, splitBooking},
    component,
    hasBookingHint
  } = props
  const isSplitBooking = Boolean(splitBooking)
  const {id: offerId} = offer || {}
  const offerOrBundleId = offer?.id || splitBooking?.bundleId
  const prices = offer?.prices || splitBooking?.prices
  const links = offer?.links || splitBooking?.links
  const analyticsContext = useSelector(state =>
    getRoomOfferAnalyticsContexts(state, offerOrBundleId, isSplitBooking)
  )
  const offerParams = getOfferCheckParams(props.offer)
  const roomlessVersion = offer ? getRoomlessVersion(room) : undefined
  const numberOfGuests = useSelector(getNumberOfGuests)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const displayRoomName = useRoomDisplayName(room?.name, {
    numberOfGuests,
    numberOfRooms
  })
  const isRoomsDetailsOverlayOpen = useSelector(getRoomDetailOverlayVisibility)
  const isDealDetailsOverlayOpen = useSelector(getDealDetailsOverlayVisibility)
  const {buildOfferCheckParams} = useGetComposedOfferCheckAPIParamsBuilder()
  const {triggerOfferCheck} = useOfferCheckContext()
  const toggle = useToggle()
  const {appDownloadLinkId} = useSelector(state =>
    getRoomOfferAppLockedConfig(state, offerId)
  )

  const flowType = useSelector(state =>
    getRoomOfferAccessConfig(state, offerId)
  )

  const isRoomOfferAppLocked = flowType === OfferAccessFlow.AppLocked
  const shouldConfirmBookOnApp =
    useSelector(state => getShouldConfirmBookOnApp(state, hotelId, offer)) &&
    isRoomOfferAppLocked
  const showOverlay = useShowOverlay()

  const isOfferCheckOnPpEnabled = toggle(
    'feature-offer-check-on-pp',
    false,
    true
  )

  const login = useLogIn()

  const anonymousId = getAnonymousId()

  const {brandCode} = useBrand()
  const {auth, tracking, commerce} = useBrandCapabilities()
  const isCoBranded = commerce.coBranded.enabled
  const brand = getBrandByCode(brandCode)
  const subBrand = getSubBrandKeyFromUrl(brand)
  const commonPaymentPageParams = useSelector(getCommonPaymentPageLandingParams)
  const isSignInReminderEligible = useIsOptionalSignInEligible()

  // Regular deals route through SUSI before checkout, optionally on Vio, where
  // the overlay offers "Continue as guest", and always on a gated brand. The
  // locked flows have their own sign-in step and never reach this one.
  const shouldShowOptionalSignIn =
    flowType === OfferAccessFlow.RegularDeal && isSignInReminderEligible

  const paymentPageParams = {
    roomId,
    ...commonPaymentPageParams,
    ...offerParams,
    ...(hasBookingHint && {bookingHint: '1'}),
    ...(subBrand && {subBrand}),
    ...(roomlessVersion && {rl: roomlessVersion})
  } as Partial<BaseUrlCheckoutParams>

  const checkoutUrl = getLinkByType(links, ActionLinkTypes.CHECKOUT)
  const activeRoomsFilters = useSelector(getRoomsActiveFilters)

  const currencyCode = useSelector(getCurrencyCode)
  const localeCode = useSelector(getLocaleCode)
  const anchorPriceTotal = useSelector(state =>
    getAnchorPriceTotal(state, hotelId)
  )
  const isGems = useSelector(state => getIsPrivateDeal(offer)(state))

  const {data: hotelData} = useSelector(state => getHotelResult(state, hotelId))
  const userCountryCode = useSelector(getUserCountryCode)
  const userId = useSelector(selectUserId)
  const whiteLabelUserId = useSelector(getWhiteLabelUserId)
  const localTaxesIncluded = useSelector(getLocalTaxesIncluded)
  const taxesIncluded = useSelector(getTaxesIncluded)
  const isUserSearch = useSelector(getIsUserSearch)
  const {
    checkIn: searchBoxCheckIn,
    checkOut: searchBoxCheckOut,
    rooms: searchBoxRooms
  } = useSelector(getSearchBoxValues)

  const {generateAppLockedDealDeepLink} = useGenerateAppDownloadUrl(Page.Search)

  const trackClickEvent = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.RoomOffer,
        action: Action.Clicked,
        team: Team.Select,
        component,
        analyticsContext,
        payload: {
          roomFilterApplied: activeRoomsFilters.length > 0,
          allSelectedRoomFilters: roomsFilterListToMap(activeRoomsFilters),
          srpSearchId: getQueryStringObject()?.srpSearchId,
          ...(room && {displayRoomName}),
          ...(isRoomOfferAppLocked ? {bannerId: appDownloadLinkId} : {})
        },
        page: Page.Accommodation
      })
    )

    if (tracking.hasCioTracking) {
      const cio = customerIo(brand)
      const hotelImageUrl = hotelData?.imageURIs?.[0]
        ? imageProvider(hotelData.imageURIs[0], SIZES['large'])
        : undefined
      const displayPrice = getDisplayPrice(prices)
      const totalRate = getTotalPrice(displayPrice)
      const round2dp = (n: number) => Math.round(n * 100) / 100

      cio.trackEvent({
        name: `${Category.User}_${Entity.RoomOffer}_${Action.Clicked}`,
        pageName: Page.Accommodation,
        userId: userId || whiteLabelUserId,
        anonymousId,
        hotelId,
        starRating: hotelData?.starRating,
        hotelName: hotelData?.hotelName,
        hotelCity: hotelData?.navPathInfo?.city?.name,
        hotelAddress: hotelData?.displayAddress,
        offerId: offerOrBundleId,
        rate: {
          base: round2dp(displayPrice?.chargeable?.base ?? 0),
          hotelFees: round2dp(displayPrice?.hotelFees?.total ?? 0),
          taxes: round2dp(displayPrice?.chargeable?.taxes ?? 0),
          localTaxesIncluded: Boolean(localTaxesIncluded),
          taxesIncluded: Boolean(taxesIncluded),
          totalRate: round2dp(totalRate ?? 0)
        },
        id: userId || whiteLabelUserId,
        checkIn: getEpochSeconds(searchBoxCheckIn),
        checkOut: getEpochSeconds(searchBoxCheckOut),
        pageUrl: window.location.href,
        bookURI: checkoutUrl,
        rooms: searchBoxRooms || '1',
        roomName: analyticsContext?.offerContext?.roomName,
        currency: currencyCode,
        country: userCountryCode,
        locale: localeCode,
        isUserSearch,
        providerCode: analyticsContext?.offerContext?.providerCode,
        proxyProviderCode: analyticsContext?.offerContext?.proxyProviderCode,
        hotelImageUrl,
        timezone: getTimezone()
      })
    }
  }, [
    activeRoomsFilters,
    appDownloadLinkId,
    component,
    dispatch,
    isRoomOfferAppLocked,
    analyticsContext,
    tracking.hasCioTracking,
    brand,
    hotelData,
    userId,
    whiteLabelUserId,
    anonymousId,
    hotelId,
    offerOrBundleId,
    localTaxesIncluded,
    taxesIncluded,
    searchBoxCheckIn,
    searchBoxCheckOut,
    checkoutUrl,
    searchBoxRooms,
    currencyCode,
    userCountryCode,
    localeCode,
    isUserSearch,
    prices,
    room,
    displayRoomName
  ])

  const showBookOnAppConfirmation = (offerCheck: OfferCheckAPIResponse) =>
    showOverlay({
      type: OverlayType.BookOnAppConfirmation,
      entity: Entity.BookOnAppConfirmation,
      component,
      analyticsContext,
      team: Team.Select,
      payload: {bannerId: appDownloadLinkId},
      params: {
        roomId,
        offerId,
        placement: appDownloadLinkId,
        clickedOfferRate: getTotalPrice(getDisplayPrice(prices)),
        currency: currencyCode,
        offerCheck: {
          checkIn: offerCheck.checkIn,
          checkOut: offerCheck.checkOut,
          prices: offerCheck.prices,
          paymentMode: offerCheck.paymentMode
        }
      }
    })

  const handleClick = () => {
    if (!checkoutUrl) {
      captureSentryError(offer, splitBooking)
      return undefined
    }

    trackClickEvent()

    const apiParams = buildOfferCheckParams({...props.offer, room})

    if (isRoomsDetailsOverlayOpen || isDealDetailsOverlayOpen) {
      dispatch(hideOverlay())
    }

    const navigate = (httpRequestId?: string) => {
      const checkoutLandingUrl = getCheckoutLink({
        checkoutUrl,
        paymentPageParams
      })
      const landingUrlWithRequestId = httpRequestId
        ? `${checkoutLandingUrl}&apRequestId=${httpRequestId}`
        : checkoutLandingUrl
      const urlWithSourceBrand = anonymousId
        ? `${landingUrlWithRequestId}&sourceBrand=${brandCode}&anonymousId=${encodeURIComponent(anonymousId)}`
        : `${landingUrlWithRequestId}&sourceBrand=${brandCode}`

      if (flowType === OfferAccessFlow.PrivateLockedDeal) {
        return login({
          anonymousId,
          flow: AuthenticationFlows.PostOfferCheck,
          sourceComponent: SourceComponentType.UnlockOverlay,
          unlockHotelId: hotelId,
          onAuthSuccessLandingUrl: urlWithSourceBrand
        })
      }

      if (flowType === OfferAccessFlow.AppLocked) {
        const appDownloadUrl = generateAppLockedDealDeepLink(
          appDownloadLinkId,
          {
            hotelId,
            clickedOfferRate: getTotalPrice(getDisplayPrice(prices)),
            currency: currencyCode
          }
        )
        navigateTo(appDownloadUrl)
        return
      }

      if (
        shouldShowOptionalSignIn &&
        (auth.requiresSignInForCheckout ||
          toggle('1cmqc2sk-login-change-on-bovio-funnel', true, false))
      ) {
        return login({
          anonymousId,
          flow: AuthenticationFlows.PreCheckout,
          sourceComponent: SourceComponentType.ApBookButton,
          onAuthSuccessLandingUrl: urlWithSourceBrand
        })
      }

      // If brand is coBranded, redirect to ProviderRedirect page first
      if (isCoBranded) {
        const totalPrice = getTotalPrice(getDisplayPrice(prices))
        const providerCode =
          offer?.providerCode ||
          splitBooking?.offers[0]?.offer?.providerCode ||
          ''
        const yourChoice = offer
          ? hasOfferTag(offer, OfferTagLabel.IsYourChoice)
          : false

        // Check if brandInfo exists (it's available on SearchOffer but not on Offer type)
        const metadataWithBrandInfo = offer?.metadata as {
          brandInfo?: {logoURL?: string; iconURL?: string}
        }
        const brandInfo = metadataWithBrandInfo?.brandInfo

        const redirectParams: ProviderRedirectQueryParamsType = {
          anchorPrice: anchorPriceTotal ?? totalPrice,
          currency: currencyCode,
          providerCode,
          redirectURI: urlWithSourceBrand,
          totalPrice: totalPrice,
          lang: localeCode,
          yourChoice,
          isGems: Boolean(isGems),
          ...(brandInfo && {
            brandInfo: encodeURIComponent(
              JSON.stringify({
                logoURL: brandInfo.logoURL,
                iconURL: brandInfo.iconURL
              })
            )
          })
        }

        // Use generateRedirectLink with isBofhRedirectHidden: false and isBofhOffer: false
        // to get the internal redirect link (ProviderRedirect page)
        const providerRedirectUrl = generateRedirectLink(
          redirectParams,
          false,
          false
        )

        setTimeout(() => {
          navigateTo(providerRedirectUrl)
        }, NAVIGATION_DELAY)
        return
      }

      setTimeout(() => {
        navigateTo(urlWithSourceBrand)
      }, NAVIGATION_DELAY)
    }

    // The confirmation must show a checked price, so it never skips the check
    if (isOfferCheckOnPpEnabled && !shouldConfirmBookOnApp) {
      navigate()
      return
    }

    triggerOfferCheck({
      apiParams,
      offerId: offerOrBundleId,
      onSuccess: ({httpRequestId, offerCheck}) =>
        shouldConfirmBookOnApp
          ? showBookOnAppConfirmation(offerCheck)
          : navigate(httpRequestId)
    })
  }

  return {handleClick}
}
